import * as THREE from 'three';
import { useMemo } from 'react';

/**
 * Traverse the hierarchy in DFS order, and return the first object
 * for which `test(obj)` is truthy.
 * If none match, returns null.
 */
function traverseUntil(
  object: THREE.Object3D,
  test: (obj: THREE.Object3D) => boolean,
): THREE.Object3D | null {
  if (test(object)) return object;

  for (const child of object.children) {
    const match = traverseUntil(child, test);
    if (match) return match;
  }

  return null;
}

const getElementByRegex = (object: THREE.Object3D, regex: RegExp): THREE.Object3D | null => {
  return traverseUntil(object, (obj) => obj.visible && regex.test(obj.name));
};

export type FootprintOpts = {
  /** Solid fill for the footprint (hex, css, or number). */
  fill?: THREE.ColorRepresentation;
  /** Background. Use 'transparent' for alpha PNG. */
  background?: THREE.ColorRepresentation | 'transparent';
  /** Extra padding around the object in world units (meters). */
  padding?: number;
  /**
   * Output size. Provide either widthPx (height inferred from aspect),
   * or pixelsPerMeter. If neither is provided, defaults to 512 px wide.
   */
  widthPx?: number;
  pixelsPerMeter?: number;

  /**
   * Align the footprint along a direction (e.g. tee→green).
   * If provided, the camera will rotate around Y so that this vector points +Z
   * (so your hole is “vertical” in the image).
   */
  alignDirection?: THREE.Vector3;

  /** Optional: override material side/depth behavior. */
  doubleSided?: boolean;
};

function cloneWithWorldMatrix(obj: THREE.Object3D): THREE.Object3D {
  // Deep clone keeping world transforms baked into matrices (no parenting).
  obj.updateWorldMatrix(true, true);
  const clone = obj.clone(true);
  clone.traverse((n) => {
    const src = obj.getObjectById(n.id);
    if (src) {
      // Keep matrixWorld baked
      n.applyMatrix4(src.matrixWorld);
      n.updateMatrixWorld(true);
    }
  });
  return clone;
}

const isLight = (obj: THREE.Object3D): obj is THREE.Light => (obj as THREE.Light).isLight;
const isHelper = (
  obj: THREE.Object3D,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): obj is THREE.Object3D & { isHelper: boolean } & { isHelper: true } => (obj as any).isHelper;
/**
 * Render a top-down orthographic PNG of an Object3D.
 * Returns a dataURL you can <img src=...> or upload.
 */
export function renderOrthographicFootprint(
  renderer: THREE.WebGLRenderer,
  source: THREE.Object3D,
  opts: FootprintOpts = {},
): string {
  const fill = opts.fill ?? '#79d329'; // nice “green”
  const background = opts.background ?? 'transparent';
  const padding = opts.padding ?? 1.0;
  const doubleSided = opts.doubleSided ?? true;

  // --- Build a temporary scene with a single, flat-colored clone ---
  const scene = new THREE.Scene();
  const group = cloneWithWorldMatrix(source);
  // Strip lights/helpers; we’ll use an override material
  group.traverse((o) => {
    if (isLight(o) || isHelper(o)) o.visible = false;
  });
  scene.add(group);

  // Optional alignment (rotate around Y so alignDirection faces +Z)
  if (opts.alignDirection) {
    const dir = opts.alignDirection.clone().setY(0).normalize();
    if (dir.lengthSq() > 0) {
      // angle from +Z to dir in XZ plane
      const theta = Math.atan2(dir.x, dir.z);
      // rotate the scene by -theta so dir becomes +Z
      const q = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), -theta);
      scene.applyQuaternion(q);
      scene.updateMatrixWorld(true);
    }
  }

  // Compute world-space bbox in XZ plane
  const box = new THREE.Box3().setFromObject(scene);
  if (!isFinite(box.min.x)) {
    // empty box protection
    return 'data:image/png;base64,';
  }
  const size = new THREE.Vector3();
  const center = new THREE.Vector3();
  box.getSize(size);
  box.getCenter(center);

  const widthMeters = size.x + padding * 2;
  const heightMeters = size.z + padding * 2; // top-down → use XZ
  const aspect = widthMeters / Math.max(1e-6, heightMeters);

  // Output size
  let widthPx = opts.widthPx ?? 512;
  if (opts.pixelsPerMeter) {
    widthPx = Math.max(16, Math.round(widthMeters * opts.pixelsPerMeter));
  }
  const heightPx = Math.max(16, Math.round(widthPx / aspect));

  // --- Camera: top-down, looking at center ---
  const cam = new THREE.OrthographicCamera(
    -widthMeters / 2,
    +widthMeters / 2,
    +heightMeters / 2,
    -heightMeters / 2,
    0.1,
    1e6,
  );
  cam.position.set(center.x, center.y + 10_000, center.z); // far above
  cam.up.set(0, 0, 1);
  cam.lookAt(center);

  // --- Override material for a flat silhouette ---
  const override = new THREE.MeshBasicMaterial({
    color: new THREE.Color(fill),
    side: doubleSided ? THREE.DoubleSide : THREE.FrontSide,
    depthTest: true,
    depthWrite: true,
  });
  const prevOverride = scene.overrideMaterial;
  scene.overrideMaterial = override;

  // Save & set renderer state
  const prevTarget = renderer.getRenderTarget();
  const prevClear = renderer.getClearColor(new THREE.Color()).clone();
  const prevAlpha = renderer.getClearAlpha();
  const prevAutoClear = renderer.autoClear;

  const rt = new THREE.WebGLRenderTarget(widthPx, heightPx, {
    minFilter: THREE.LinearFilter,
    magFilter: THREE.LinearFilter,
    format: THREE.RGBAFormat,
    depthBuffer: true,
    stencilBuffer: false,
  });

  renderer.setRenderTarget(rt);
  if (background === 'transparent') {
    renderer.setClearColor(0x000000, 0);
  } else {
    renderer.setClearColor(new THREE.Color(background), 1);
  }
  renderer.autoClear = true;
  renderer.clear(true, true, true);

  // Render
  renderer.render(scene, cam);

  // Read pixels to a canvas for dataURL
  const canvas = document.createElement('canvas');
  canvas.width = widthPx;
  canvas.height = heightPx;
  const ctx = canvas.getContext('2d')!;
  const pixels = new Uint8Array(widthPx * heightPx * 4);
  renderer.readRenderTargetPixels(rt, 0, 0, widthPx, heightPx, pixels);

  // Put imageData (note: WebGL origin is bottom-left; flip Y)
  const img = new ImageData(widthPx, heightPx);
  for (let y = 0; y < heightPx; y++) {
    const srcRow = (heightPx - 1 - y) * widthPx * 4;
    const dstRow = y * widthPx * 4;
    img.data.set(pixels.subarray(srcRow, srcRow + widthPx * 4), dstRow);
  }
  ctx.putImageData(img, 0, 0);

  // Restore renderer state
  scene.overrideMaterial = prevOverride ?? null;
  renderer.setRenderTarget(prevTarget);
  renderer.setClearColor(prevClear, prevAlpha);
  renderer.autoClear = prevAutoClear;

  rt.dispose();
  override.dispose();

  return canvas.toDataURL('image/png');
}

function directionTeeToGreen(hole: THREE.Object3D) {
  const tee = getElementByRegex(hole, new RegExp('_tee$'));
  const green = getElementByRegex(hole, new RegExp('_green$'));
  if (!tee || !green) return undefined;
  const a = new THREE.Vector3();
  const b = new THREE.Vector3();
  tee.getWorldPosition(a);
  green.getWorldPosition(b);
  return b.sub(a).setY(0).normalize();
}

export function useHoleFootprint() {
  const gl = useMemo(() => new THREE.WebGLRenderer({ antialias: true, alpha: true }), []);

  return (
    hole: THREE.Object3D,
    opts?: Partial<Parameters<typeof renderOrthographicFootprint>[2]>,
  ) => {
    const dir = directionTeeToGreen(hole);
    const url = renderOrthographicFootprint(gl, getElementByRegex(hole, /green/)!, {
      fill: '#7bd328',
      background: 'transparent',
      padding: 2,
      widthPx: 256,
      alignDirection: dir,
      ...opts,
    });
    return url;
  };
}
